// scoreBreakdown.js
//
// Explains a team's raw weekly fantasy score (before the captain/5x
// multiplier) as a list of line items instead of one number, for the Lineup
// tab's "how was this score made" panel.
//
// This is a line-item mirror of backend/scoring.js's calculateTeamFantasyPoints,
// the scorer of record. Every branch and every threshold below must match it
// exactly: src/utils/__tests__/scoreBreakdown.test.js requires that file
// directly and checks this function's lines always sum to what it returns,
// across a wide sweep of inputs. If that test fails, this file drifted, not
// the test.
import { SUPER_DOG_SPREAD } from "./season";

/**
 * @param {{won: boolean, tied?: boolean, spread: number|null, actualMargin: number, isLive?: boolean}} args
 *   spread is already from this team's own side: negative means favoured,
 *   positive means underdog, matching getTeamSpread in gameStatus.js.
 *   tied only ever applies to a live, in-progress game: CFB games cannot
 *   finish level. isLive only changes wording, never a threshold or a point
 *   value: a live result is provisional, so its lines read as something
 *   still happening ("Covering by 3") rather than a settled fact ("Covered
 *   by 3"), the same way the rest of the app already marks a live score
 *   (isGameLive's callers, the LIVE tag on the row above this panel).
 * @returns {{lines: {label: string, points: number}[], total: number}}
 */
export function explainTeamFantasyPoints({ won, tied = false, spread, actualMargin, isLive = false }) {
  const lines = [];
  const hasSpread = spread !== null && spread !== undefined;
  const isUnderdog = hasSpread && spread > 0;

  if (tied) {
    lines.push({ label: "Tied right now", points: 0 });
  } else if (won) {
    lines.push({ label: isLive ? "Winning right now" : "Win", points: 5 });
  } else {
    lines.push({ label: isLive ? "Losing right now" : "Loss", points: -3 });
  }

  if (isUnderdog && won) {
    lines.push({ label: "Underdog win bonus", points: 3 });
  }

  // Super Dog cancels the loss penalty for a huge underdog. A tie already
  // starts from 0, so there is no penalty left to cancel there.
  if (hasSpread && !won && !tied && spread >= SUPER_DOG_SPREAD) {
    lines.push({ label: `Super Dog, underdog by ${spread} or more`, points: 3 });
  }

  if (hasSpread) {
    const coverPoints = spread < 0 ? actualMargin - Math.abs(spread)
      : spread > 0 ? actualMargin + spread
      : actualMargin;
    const covering = isLive ? "Covering" : "Covered";
    const missing = isLive ? "Missing" : "Missed";

    if (Math.abs(coverPoints) < 0.5) {
      lines.push({ label: isLive ? "On the number right now" : "Pushed against the spread", points: 0 });
    } else if (coverPoints >= 20) {
      lines.push({ label: `${covering} by ${coverPoints} or more`, points: 5 });
    } else if (coverPoints >= 14.5) {
      lines.push({ label: `${covering} by ${coverPoints}`, points: 3 });
    } else if (coverPoints >= 7.5) {
      lines.push({ label: `${covering} by ${coverPoints}`, points: 2 });
    } else if (coverPoints >= 0.5) {
      lines.push({ label: `${covering} by ${coverPoints}`, points: 1 });
    } else if (coverPoints >= -7) {
      lines.push({ label: `${missing} the spread by ${Math.abs(coverPoints)}`, points: -1 });
    } else if (coverPoints >= -14) {
      lines.push({ label: `${missing} the spread by ${Math.abs(coverPoints)}`, points: -2 });
    } else if (coverPoints > -20) {
      lines.push({ label: `${missing} the spread by ${Math.abs(coverPoints)}`, points: -3 });
    } else {
      lines.push({ label: `${missing} the spread by ${Math.abs(coverPoints)} or more`, points: -5 });
    }
  }

  const total = lines.reduce((sum, l) => sum + l.points, 0);
  return { lines, total };
}

// One line for the captain/5x multiplier, to sit under the breakdown above.
// Mirrors calculateCombinedPoints in gameStatus.js, which is what actually
// computes the displayed number; this only describes it in words.
export function describeMultiplier({ isCaptain, isTripPlay }) {
  if (isCaptain && isTripPlay) return { label: "Captain and 5x Play", factor: 5 };
  if (isCaptain) return { label: "Captain", factor: 2 };
  if (isTripPlay) return { label: "5x Play", factor: 3 };
  return null;
}
